import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import {
  Gift, Link as LinkIcon, Facebook, DollarSign, Calendar,
  Rocket, CheckCircle, AlertCircle, ChevronRight, ChevronLeft,
  Eye, Target, TrendingUp, Zap, Shield, Sparkles, ExternalLink,
  Users, Globe, Clock, ArrowRight, Play, Loader2, Info,
  Image as ImageIcon, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

/* ═══════════════════════════════════════════════════════════════
   GAPLI CAMPAIGN TEMPLATE — identical to active Gaplidrop campaign
   ═══════════════════════════════════════════════════════════════ */
const GAPLI_CAMPAIGN_TEMPLATE = {
  name: 'Rejestracja w Gapli - Nowi użytkownicy',
  objective: 'OUTCOME_TRAFFIC' as const,
  call_to_action: 'LEARN_MORE',
  source_type: 'gapli_partner',
  targeting: {
    age_min: 20,
    age_max: 55,
    genders: [0], // All
    geo_locations: { countries: ['PL'] },
  },
  creatives: [
    {
      primary_text: '🚀 Odkryj nowe możliwości z Gapli! Platforma, która zmieni sposób w jaki pracujesz. Dołącz do tysięcy zadowolonych użytkowników już dziś! ✨ Rejestracja zajmuje tylko 2 minuty.',
      headline: '🚀 Zacznij sprzedawać bez magazynu',
      description: 'Gapli — platforma dropshippingowa z ponad 1.1 mln produktów. Automatyczna obsługa zamówień, integracja z Allegro i WooCommerce. Zacznij za darmo, bez ryzyka!',
      media_url: 'https://xahssxqgkrrlotaunwtg.supabase.co/storage/v1/object/public/public-assets/gapli-ad-creative.png',
      media_type: 'image' as const,
    },
  ],
};

/* ═══════════════════════════════════════════════════════════════ */

interface SocialAccount {
  id: string;
  page_id: string;
  page_name: string;
  platform: string;
  access_token: string | null;
  meta_access_token: string | null;
  meta_ad_account_id: string | null;
}

export default function PromoteGapli() {
  const { user } = useAuth();

  // ── State ──
  const [step, setStep] = useState(0);
  const [partnerLink, setPartnerLink] = useState('');
  const [linkError, setLinkError] = useState('');
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [dailyBudget, setDailyBudget] = useState(20);
  const [durationDays, setDurationDays] = useState(14);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<{ success: boolean; message: string; campaignId?: string } | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [hasAdsSettings, setHasAdsSettings] = useState(false);

  // ── Derived ──
  const totalBudget = dailyBudget * durationDays;
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + durationDays);

  // ── Effects ──
  useEffect(() => {
    if (user) {
      fetchAccounts();
      checkAdsSettings();
    }
  }, [user]);

  const fetchAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const { data } = await supabase
        .from('social_accounts')
        .select('id, page_id, page_name, platform, access_token, meta_access_token, meta_ad_account_id')
        .eq('owner_user_id', user?.id)
        .eq('platform', 'facebook')
        .eq('status', 'active');
      if (data && data.length > 0) {
        setSocialAccounts(data);
        setSelectedPageId(data[0].page_id);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const checkAdsSettings = async () => {
    const { data } = await supabase
      .from('ad_booster_settings')
      .select('meta_access_token, meta_ad_account_id')
      .eq('user_id', user?.id)
      .maybeSingle();
    setHasAdsSettings(!!(data?.meta_access_token && data?.meta_ad_account_id));
  };

  // ── Validation ──
  const validateLink = useCallback((url: string) => {
    if (!url.trim()) { setLinkError(''); return false; }
    try {
      const parsed = new URL(url);
      if (!parsed.protocol.startsWith('http')) {
        setLinkError('Link musi zaczynać się od https://');
        return false;
      }
      setLinkError('');
      return true;
    } catch {
      setLinkError('Podaj poprawny adres URL (np. https://gapli.pl/ref/twoj-kod)');
      return false;
    }
  }, []);

  const canProceed = (s: number): boolean => {
    if (s === 0) return validateLink(partnerLink);
    if (s === 1) return !!selectedPageId && hasAdsSettings;
    if (s === 2) return dailyBudget >= 5 && durationDays >= 1;
    return true;
  };

  const goNext = () => {
    if (!canProceed(step)) {
      if (step === 0) toast.error('Podaj poprawny link partnerski');
      if (step === 1 && !selectedPageId) toast.error('Wybierz stronę Facebook');
      if (step === 1 && !hasAdsSettings) toast.error('Skonfiguruj Meta Ads w zakładce Marketing → Ustawienia Ads');
      if (step === 2 && dailyBudget < 5) toast.error('Minimalny budżet to 5 PLN/dzień');
      return;
    }
    setStep(s => Math.min(3, s + 1));
  };
  const goBack = () => setStep(s => Math.max(0, s - 1));

  // ── Publish Campaign ──
  const publishCampaign = async () => {
    if (!user || !selectedPageId) return;
    try {
      setIsPublishing(true);
      toast.loading('🚀 Tworzenie kampanii Gapli w Meta Ads...', { id: 'gapli-pub' });

      const startDate = new Date();

      // 1. Insert campaign
      const { data: camp, error: campErr } = await supabase.from('ad_campaigns').insert({
        user_id: user.id,
        name: GAPLI_CAMPAIGN_TEMPLATE.name,
        objective: GAPLI_CAMPAIGN_TEMPLATE.objective,
        daily_budget: dailyBudget,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        destination_url: partnerLink,
        targeting: GAPLI_CAMPAIGN_TEMPLATE.targeting,
        status: 'draft',
        source_type: GAPLI_CAMPAIGN_TEMPLATE.source_type,
        call_to_action: GAPLI_CAMPAIGN_TEMPLATE.call_to_action,
        meta_page_id: selectedPageId,
      }).select('id').single();

      if (campErr || !camp) throw new Error(campErr?.message || 'Błąd tworzenia kampanii');

      // 2. Insert creatives
      const creativesPayload = GAPLI_CAMPAIGN_TEMPLATE.creatives.map((c, i) => ({
        campaign_id: camp.id,
        media_url: c.media_url,
        media_type: c.media_type,
        primary_text: c.primary_text,
        headline: c.headline,
        description: c.description,
        sort_order: i,
      }));

      const { error: crErr } = await supabase.from('ad_campaign_creatives').insert(creativesPayload);
      if (crErr) throw new Error(crErr.message);

      // 3. Call publisher edge function
      toast.loading('📡 Publikacja w Meta Ads...', { id: 'gapli-pub' });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Brak sesji — zaloguj się ponownie');

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaign-publisher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ campaign_id: camp.id }),
      });

      let result: any;
      try { result = await res.json(); } catch { throw new Error('Serwer nie odpowiedział prawidłowo'); }

      if (!res.ok) {
        throw new Error(result.error || 'Publikacja nie powiodła się');
      }

      if (result.success) {
        toast.success('✅ Kampania Gapli jest AKTYWNA w Meta Ads!', { id: 'gapli-pub', duration: 6000 });
        setPublishResult({ success: true, message: 'Kampania została pomyślnie opublikowana i jest aktywna!', campaignId: camp.id });
        setStep(4); // success step
      } else {
        toast.success('Kampania utworzona. Sprawdź status w zakładce Marketing.', { id: 'gapli-pub', duration: 5000 });
        setPublishResult({ success: true, message: 'Kampania została utworzona. Sprawdź status w zakładce Marketing → Aktywne Kampanie.', campaignId: camp.id });
        setStep(4);
      }
    } catch (error) {
      console.error('[Gapli Campaign]', error);
      const msg = error instanceof Error ? error.message : 'Nieznany błąd';
      toast.error(msg, { id: 'gapli-pub' });
      setPublishResult({ success: false, message: msg });
    } finally {
      setIsPublishing(false);
    }
  };

  // ── Steps config ──
  const steps = [
    { label: 'Link Partnerski', icon: LinkIcon, desc: 'Twój link polecający' },
    { label: 'Facebook', icon: Facebook, desc: 'Konto reklamowe' },
    { label: 'Budżet', icon: DollarSign, desc: 'Budżet i czas trwania' },
    { label: 'Publikacja', icon: Rocket, desc: 'Podgląd i start' },
  ];

  // ── Styles ──
  const inputCls = "w-full px-5 py-4 bg-[#1e293b]/60 border border-slate-700/80 rounded-2xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/60 focus:border-purple-500/40 transition-all text-lg";

  return (
    <div className="space-y-8 pb-16 bg-[#020617] min-h-screen text-slate-200 p-1 rounded-3xl">

      {/* ═══════ HERO HEADER ═══════ */}
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#1a0533] via-[#0f0a2e] to-[#020617] p-8 md:p-12 border border-purple-900/40 shadow-2xl">
        {/* Animated blobs */}
        <div className="absolute -top-32 -right-32 w-80 h-80 bg-purple-600/25 rounded-full blur-[100px] pointer-events-none animate-pulse" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-fuchsia-600/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-3xl">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-500 p-[2px] shadow-lg shadow-purple-500/30">
              <div className="w-full h-full bg-[#0f0a2e] rounded-2xl flex items-center justify-center">
                <Gift className="w-8 h-8 text-purple-400" />
              </div>
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-fuchsia-200 to-pink-200 tracking-tight">
                Promuj Gapli
              </h1>
              <p className="text-purple-300/60 text-sm font-semibold mt-0.5">Partner Promotion System</p>
            </div>
          </div>
          <p className="text-lg text-slate-400 leading-relaxed font-medium max-w-xl">
            Opublikuj profesjonalną kampanię reklamową Gapli na Facebooku za pomocą
            <span className="text-purple-300 font-bold"> jednego kliknięcia</span>.
            Podaj swój link partnerski, ustaw budżet i gotowe!
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { icon: Zap, text: 'Gotowy szablon' },
              { icon: Target, text: 'Precyzyjne targetowanie' },
              { icon: Shield, text: 'Meta Ads API v21.0' },
              { icon: TrendingUp, text: 'Gotowa kreacja' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm font-semibold">
                <f.icon className="w-4 h-4" /> {f.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════ WIZARD STEPPER ═══════ */}
      <div className="flex items-center gap-1 md:gap-2 px-2">
        {steps.map((s, i) => {
          const StepIcon = s.icon;
          const isCompleted = i < step;
          const isCurrent = i === step;
          const isSuccess = step === 4;
          return (
            <div key={i} className="flex items-center flex-1">
              <button
                onClick={() => { if (i < step && step < 4) setStep(i); }}
                className={`flex items-center gap-2 px-3 py-3 rounded-xl transition-all w-full ${
                  isSuccess ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400' :
                  isCurrent ? 'bg-purple-600/20 border border-purple-500/50 text-white shadow-lg shadow-purple-500/10' :
                  isCompleted ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 cursor-pointer hover:bg-emerald-500/20' :
                  'bg-slate-800/50 border border-slate-800 text-slate-500'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black shrink-0 ${
                  isSuccess ? 'bg-emerald-500/20 text-emerald-400' :
                  isCurrent ? 'bg-purple-600 text-white' :
                  isCompleted ? 'bg-emerald-500/20 text-emerald-400' :
                  'bg-slate-800 text-slate-600'
                }`}>
                  {isCompleted || isSuccess ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                </div>
                <div className="hidden md:block text-left min-w-0">
                  <p className="text-xs font-bold truncate">{s.label}</p>
                  <p className="text-[10px] opacity-60 truncate">{s.desc}</p>
                </div>
              </button>
              {i < steps.length - 1 && (
                <div className={`w-4 md:w-8 h-0.5 shrink-0 mx-1 rounded-full transition-colors ${
                  isCompleted ? 'bg-emerald-500/40' : 'bg-slate-800'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* ═══════ STEP CONTENT ═══════ */}
      <div className="min-h-[450px]">

        {/* ── STEP 0: Partner Link ── */}
        {step === 0 && (
          <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-right-4">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-800/80 p-8 md:p-10 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-fuchsia-500/20 border border-purple-500/30 flex items-center justify-center">
                  <LinkIcon className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Twój Link Partnerski</h2>
                  <p className="text-sm text-slate-400">Wklej link polecający z platformy Gapli</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="url"
                    placeholder="https://gapli.pl/ref/twoj-kod"
                    value={partnerLink}
                    onChange={(e) => { setPartnerLink(e.target.value); validateLink(e.target.value); }}
                    className={`${inputCls} pr-12 ${linkError ? 'border-rose-500/60 focus:ring-rose-500/40' : partnerLink && !linkError ? 'border-emerald-500/60 focus:ring-emerald-500/40' : ''}`}
                  />
                  {partnerLink && !linkError && (
                    <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-emerald-400" />
                  )}
                  {linkError && (
                    <AlertCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-rose-400" />
                  )}
                </div>
                {linkError && <p className="text-sm text-rose-400 font-semibold pl-1">{linkError}</p>}

                <div className="bg-purple-500/8 border border-purple-500/20 rounded-2xl p-5 mt-6">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm text-purple-200 font-semibold mb-1">Jak uzyskać link partnerski?</p>
                      <p className="text-xs text-purple-300/70 leading-relaxed">
                        Zaloguj się na swoje konto Gapli → przejdź do sekcji "Program Partnerski" lub "Polecaj" →
                        skopiuj swój unikalny link polecający. Każda osoba, która zarejestruje się przez Twój link,
                        przyniesie Ci prowizję!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 1: Facebook Account ── */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-right-4">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-800/80 p-8 md:p-10 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 flex items-center justify-center">
                  <Facebook className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Konto Facebook</h2>
                  <p className="text-sm text-slate-400">Wybierz stronę, z której wyemitujemy reklamę</p>
                </div>
              </div>

              {loadingAccounts ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-12 h-12 border-4 border-slate-700 border-t-purple-500 rounded-full animate-spin" />
                </div>
              ) : socialAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <Facebook className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Brak podłączonych stron</h3>
                  <p className="text-slate-400 mb-6 max-w-md mx-auto">
                    Aby opublikować kampanię, musisz najpierw podłączyć swoją stronę Facebook
                    w sekcji Social Inbox lub Ustawienia.
                  </p>
                  <button
                    onClick={() => {
                      window.history.pushState({}, '', '/social/accounts');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all inline-flex items-center gap-2"
                  >
                    <Facebook className="w-5 h-5" /> Podłącz Facebook
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {socialAccounts.map(acc => (
                    <button
                      key={acc.id}
                      onClick={() => setSelectedPageId(acc.page_id)}
                      className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 transition-all ${
                        selectedPageId === acc.page_id
                          ? 'border-purple-500 bg-purple-500/10 shadow-lg shadow-purple-500/10'
                          : 'border-slate-800 bg-[#1e293b]/30 hover:border-slate-600 hover:bg-[#1e293b]/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                        selectedPageId === acc.page_id
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-800 text-slate-400'
                      }`}>
                        <Facebook className="w-6 h-6" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-bold text-white text-lg">{acc.page_name}</p>
                        <p className="text-xs text-slate-500 font-mono">ID: {acc.page_id}</p>
                      </div>
                      {selectedPageId === acc.page_id && (
                        <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center shrink-0">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </button>
                  ))}

                  {!hasAdsSettings && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 mt-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm text-amber-200 font-semibold mb-1">Konfiguracja Meta Ads wymagana</p>
                          <p className="text-xs text-amber-300/70 leading-relaxed mb-3">
                            Przejdź do zakładki Marketing → Ustawienia Ads, aby skonfigurować
                            Meta Ads API (App ID, App Secret, Ad Account ID, Access Token).
                          </p>
                          <button
                            onClick={() => {
                              window.history.pushState({}, '', '/ads');
                              window.dispatchEvent(new PopStateEvent('popstate'));
                            }}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-lg text-sm inline-flex items-center gap-2 transition-all"
                          >
                            Przejdź do Ustawień <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 2: Budget & Duration ── */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-right-4">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-800/80 p-8 md:p-10 shadow-xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Budżet i Czas Trwania</h2>
                  <p className="text-sm text-slate-400">Ustaw parametry finansowe kampanii</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Daily Budget */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-300 mb-3">
                    <DollarSign className="w-4 h-4 text-emerald-400" /> Budżet dzienny
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={5}
                      max={5000}
                      value={dailyBudget}
                      onChange={e => setDailyBudget(Math.max(5, Number(e.target.value)))}
                      className={inputCls}
                    />
                    <span className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 font-bold">PLN/dzień</span>
                  </div>
                  {/* Quick presets */}
                  <div className="flex gap-2 mt-3">
                    {[10, 20, 50, 100].map(v => (
                      <button
                        key={v}
                        onClick={() => setDailyBudget(v)}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                          dailyBudget === v
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white border border-slate-700'
                        }`}
                      >
                        {v} PLN
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-300 mb-3">
                    <Calendar className="w-4 h-4 text-blue-400" /> Czas trwania
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={durationDays}
                      onChange={e => setDurationDays(Math.max(1, Math.min(90, Number(e.target.value))))}
                      className={inputCls}
                    />
                    <span className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 font-bold">dni</span>
                  </div>
                  {/* Quick presets */}
                  <div className="flex gap-2 mt-3">
                    {[7, 14, 30, 60].map(v => (
                      <button
                        key={v}
                        onClick={() => setDurationDays(v)}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                          durationDays === v
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white border border-slate-700'
                        }`}
                      >
                        {v} dni
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Total Cost Calculator */}
              <div className="bg-gradient-to-r from-purple-500/10 via-fuchsia-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20 p-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Budżet / dzień</p>
                    <p className="text-2xl font-black text-emerald-400">{dailyBudget} PLN</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Okres</p>
                    <p className="text-2xl font-black text-blue-400">{durationDays} dni</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Koszt całkowity</p>
                    <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-400">{totalBudget} PLN</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-purple-500/20">
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Kampania od <strong className="text-slate-300">{new Date().toLocaleDateString('pl-PL')}</strong> do <strong className="text-slate-300">{endDate.toLocaleDateString('pl-PL')}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Preview & Publish ── */}
        {step === 3 && (
          <div className="max-w-3xl mx-auto space-y-6 animate-in fade-in slide-in-from-right-4">
            <div className="bg-[#0f172a] rounded-3xl border border-slate-800/80 p-8 md:p-10 shadow-xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Podgląd Kampanii</h2>
                  <p className="text-sm text-slate-400">Sprawdź wszystko przed publikacją</p>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Cel', value: 'Ruch', icon: Target, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
                  { label: 'Budżet / dzień', value: `${dailyBudget} PLN`, icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                  { label: 'Okres', value: `${durationDays} dni`, icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                  { label: 'Koszt całk.', value: `${totalBudget} PLN`, icon: TrendingUp, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                ].map((s, i) => (
                  <div key={i} className="bg-[#0b0f19] p-4 rounded-2xl border border-slate-800/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`p-1.5 rounded-lg ${s.bg}`}><s.icon className={`w-3.5 h-3.5 ${s.color}`} /></div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{s.label}</span>
                    </div>
                    <p className="text-lg font-black text-white">{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Campaign Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-[#0b0f19] p-5 rounded-2xl border border-slate-800/50">
                  <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><LinkIcon className="w-3 h-3" /> Link docelowy</p>
                  <p className="text-sm text-purple-300 font-mono break-all">{partnerLink}</p>
                </div>
                <div className="bg-[#0b0f19] p-5 rounded-2xl border border-slate-800/50">
                  <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><Facebook className="w-3 h-3" /> Strona Facebook</p>
                  <p className="text-sm text-white font-bold">{socialAccounts.find(a => a.page_id === selectedPageId)?.page_name || 'Nieznana'}</p>
                </div>
              </div>

              {/* Targeting */}
              <div className="bg-[#0b0f19] p-5 rounded-2xl border border-slate-800/50 mb-8">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-3 flex items-center gap-1.5"><Target className="w-3 h-3" /> Targetowanie</p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-3 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold flex items-center gap-1.5">
                    <Globe className="w-3 h-3" /> Polska
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs font-bold flex items-center gap-1.5">
                    <Users className="w-3 h-3" /> {GAPLI_CAMPAIGN_TEMPLATE.targeting.age_min}-{GAPLI_CAMPAIGN_TEMPLATE.targeting.age_max} lat
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold flex items-center gap-1.5">
                    <Users className="w-3 h-3" /> Wszyscy
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/20 text-fuchsia-300 text-xs font-bold flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> 1 kreacja
                  </span>
                </div>
              </div>

              {/* Creative Preview */}
              <div className="mb-8">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-4 flex items-center gap-1.5"><ImageIcon className="w-3 h-3" /> Kreacje reklamowe ({GAPLI_CAMPAIGN_TEMPLATE.creatives.length})</p>
                <div className="space-y-4">
                  {GAPLI_CAMPAIGN_TEMPLATE.creatives.map((cr, i) => (
                    <div key={i} className="bg-[#0b0f19] rounded-2xl border border-slate-800/50 overflow-hidden">
                      {/* Ad image preview */}
                      <div className="relative aspect-square max-h-[400px] bg-[#080c14] flex items-center justify-center">
                        <img
                          src={cr.media_url}
                          alt="Kreacja reklamowa Gapli"
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-sm border border-slate-700/50 text-[10px] font-bold text-emerald-400 uppercase">
                          Podgląd kreacji
                        </div>
                      </div>
                      {/* Creative text content */}
                      <div className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-lg bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-purple-400 text-sm font-black shrink-0">
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white mb-1.5">{cr.headline}</p>
                            <p className="text-xs text-slate-400 leading-relaxed mb-3">{cr.primary_text}</p>
                            {cr.description && (
                              <div className="bg-slate-800/40 rounded-xl px-4 py-3 border border-slate-700/40">
                                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Opis linku</p>
                                <p className="text-xs text-slate-300 leading-relaxed">{cr.description}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA section */}
              <div className="bg-gradient-to-r from-purple-500/10 via-fuchsia-500/10 to-pink-500/10 rounded-2xl border border-purple-500/25 p-6 text-center">
                <Rocket className="w-10 h-10 text-purple-400 mx-auto mb-3" />
                <p className="text-lg font-bold text-white mb-2">Wszystko gotowe!</p>
                <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
                  Po kliknięciu "Opublikuj" kampania zostanie automatycznie utworzona
                  i aktywowana w Meta Ads z Twoim linkiem partnerskim.
                </p>
                <button
                  onClick={publishCampaign}
                  disabled={isPublishing}
                  className={`px-10 py-4 rounded-2xl font-bold text-lg inline-flex items-center gap-3 transition-all ${
                    isPublishing
                      ? 'bg-purple-600/40 text-purple-200 cursor-wait'
                      : 'bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 text-white hover:shadow-[0_0_40px_rgba(168,85,247,0.4)] hover:scale-[1.02] active:scale-[0.98]'
                  }`}
                >
                  {isPublishing ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      Publikowanie...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-6 h-6" />
                      Opublikuj Kampanię Gapli
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 4: Success ── */}
        {step === 4 && publishResult && (
          <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4">
            <div className={`bg-[#0f172a] rounded-3xl border p-10 md:p-14 shadow-2xl text-center ${
              publishResult.success ? 'border-emerald-500/40' : 'border-rose-500/40'
            }`}>
              {publishResult.success ? (
                <>
                  {/* Success animation */}
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
                    <div className="relative w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30">
                      <CheckCircle className="w-12 h-12 text-white" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-extrabold text-white mb-3">Kampania Aktywna! 🎉</h2>
                  <p className="text-slate-400 text-lg mb-8 max-w-md mx-auto">{publishResult.message}</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                      onClick={() => {
                        window.history.pushState({}, '', '/ads');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all inline-flex items-center gap-2"
                    >
                      <Eye className="w-5 h-5" /> Zobacz w Marketing Studio
                    </button>
                    <button
                      onClick={() => { setStep(0); setPartnerLink(''); setPublishResult(null); }}
                      className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl border border-slate-700 transition-all inline-flex items-center gap-2"
                    >
                      <Gift className="w-5 h-5" /> Utwórz kolejną
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 bg-rose-500/20 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-10 h-10 text-rose-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">Błąd publikacji</h2>
                  <p className="text-rose-300 mb-8 max-w-md mx-auto">{publishResult.message}</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                      onClick={() => { setStep(3); setPublishResult(null); }}
                      className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-all inline-flex items-center gap-2"
                    >
                      <Play className="w-5 h-5" /> Spróbuj ponownie
                    </button>
                    <button
                      onClick={() => {
                        window.history.pushState({}, '', '/ads');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl border border-slate-700 transition-all inline-flex items-center gap-2"
                    >
                      Sprawdź ustawienia Ads <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ═══════ NAVIGATION BUTTONS ═══════ */}
      {step < 4 && (
        <div className="flex items-center justify-between max-w-2xl mx-auto px-2">
          <button
            onClick={goBack}
            disabled={step === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
              step === 0
                ? 'opacity-0 pointer-events-none'
                : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
            }`}
          >
            <ChevronLeft className="w-5 h-5" /> Wstecz
          </button>

          {step < 3 ? (
            <button
              onClick={goNext}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white font-bold rounded-xl shadow-lg shadow-purple-500/20 transition-all hover:shadow-purple-500/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              Dalej <ChevronRight className="w-5 h-5" />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
